import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, fmtEur } from "@/lib/api";
import { pushAnalyticsEvent } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Upload, X, Trash2, FileText, Check, AlertCircle, Search } from "lucide-react";
import SignatureCanvas from "react-signature-canvas";
import { PayPalScriptProvider, PayPalButtons, PayPalMessages } from "@paypal/react-paypal-js";
import AddressAutocomplete from "@/components/app/AddressAutocomplete";

const STEPS = [
  "Organisme",
  "Contrat",
  "Personnel",
  "Motif",
  "Analyse",
  "Documents",
  "Lettre",
  "Signature",
  "Offre",
  "Paiement",
  "Envoi",
];

const REASONS = [
  { v: "echeance", l: "Arrivée à échéance" },
  { v: "fin_engagement", l: "Fin d'engagement" },
  { v: "un_an", l: "Après un an (loi Hamon/Chatel)" },
  { v: "demenagement", l: "Déménagement" },
  { v: "vente", l: "Vente du bien" },
  { v: "changement_situation", l: "Changement de situation" },
  { v: "augmentation", l: "Augmentation tarifaire" },
  { v: "modification", l: "Modification du contrat" },
  { v: "nouvel_assureur", l: "Changement d'assureur" },
  { v: "service_defaillant", l: "Service défaillant" },
  { v: "retractation", l: "Rétractation" },
  { v: "autre", l: "Autre" },
];

export default function TerminationAssistantPage() {
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const { caseId: caseIdParam } = useParams();
  const [caseId, setCaseId] = useState(caseIdParam || null);
  const [caseData, setCaseData] = useState(null);
  const [step, setStep] = useState(1);
  const [config, setConfig] = useState(null);

  useEffect(() => {
    api.get("/config/public")
      .then((r) => setConfig(r.data))
      .catch((err) => console.error("Failed to load public config:", err));
  }, []);

  useEffect(() => {
    if (!user && !authLoading) { nav("/connexion?redirect=/resilier"); }
  }, [user, authLoading, nav]);

  useEffect(() => {
    if (!caseId) return;
    api.get(`/cases/${caseId}`).then((r) => {
      setCaseData(r.data);
      if (r.data.step && r.data.status !== "paid") setStep(r.data.step);
      else if (r.data.status === "paid") setStep(11);
    }).catch(() => toast.error("Dossier introuvable"));
  }, [caseId]);

  const saveCase = useCallback(async (patch) => {
    if (!caseId) return;
    try {
      const { data } = await api.patch(`/cases/${caseId}`, patch);
      setCaseData(data);
    } catch (e) {
      if (e?.response?.status !== 400) toast.error("Sauvegarde échouée");
    }
  }, [caseId]);

  const goStep = (n) => {
    setStep(n);
    saveCase({ step: n });
    pushAnalyticsEvent("step_completed", { funnel_step: `step_${n - 1}`, step_number: n - 1, total_steps: 10 }, { once: false });
  };

  if (authLoading) return <div className="mx-auto max-w-4xl p-8">Chargement...</div>;
  if (!user) return null;

  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">Assistant de résiliation</div>
        <h1 className="text-3xl font-bold tracking-tight">Étape {step}/{STEPS.length} · {STEPS[step - 1]}</h1>
        <Progress value={(step / STEPS.length) * 100} className="mt-4 h-1.5" data-testid="assistant-progress" />
      </div>

      {!caseId ? (
        <StepPickOrg onCreate={(id, nextStep) => { setCaseId(id); if (nextStep) setStep(nextStep); nav(`/resilier/${id}`, { replace: true }); pushAnalyticsEvent("termination_started", {}, { once: false }); }} />
      ) : caseData ? (
        <div>
          {step === 1 && <StepPickOrg current={caseData} onSelect={(patch) => { saveCase({ ...patch, step: 2 }); setStep(2); }} inline />}
          {step === 2 && <StepContract caseData={caseData} onNext={(patch) => { saveCase({ ...patch, step: 3 }); setStep(3); }} onBack={() => goStep(1)} />}
          {step === 3 && <StepPersonal caseData={caseData} onNext={(patch) => { saveCase({ ...patch, step: 4 }); setStep(4); }} onBack={() => goStep(2)} />}
          {step === 4 && <StepReason caseData={caseData} onNext={(patch) => { saveCase({ ...patch, step: 5 }); setStep(5); }} onBack={() => goStep(3)} />}
          {step === 5 && <StepAnalysis caseData={caseData} onNext={() => goStep(6)} onBack={() => goStep(4)} />}
          {step === 6 && <StepDocuments caseId={caseId} caseData={caseData} refresh={() => api.get(`/cases/${caseId}`).then((r) => setCaseData(r.data))} onNext={() => goStep(7)} onBack={() => goStep(5)} />}
          {step === 7 && <StepLetter caseId={caseId} caseData={caseData} refresh={() => api.get(`/cases/${caseId}`).then((r) => setCaseData(r.data))} onNext={() => goStep(8)} onBack={() => goStep(6)} />}
          {step === 8 && <StepSignature caseData={caseData} onNext={(patch) => { saveCase({ ...patch, step: 9 }); setStep(9); }} onBack={() => goStep(7)} />}
          {step === 9 && <StepOffer caseId={caseId} caseData={caseData} saveCase={saveCase} onBack={() => goStep(8)} />}
          {step === 10 && <StepPayment caseId={caseId} caseData={caseData} config={config} refresh={() => api.get(`/cases/${caseId}`).then((r) => setCaseData(r.data))} onSuccess={() => setStep(11)} onBack={() => goStep(9)} />}
          {step === 11 && <StepDone caseData={caseData} />}
        </div>
      ) : (
        <div>Chargement du dossier...</div>
      )}
    </div>
  );
}

// ---- STEP 1: pick organization ----
function StepPickOrg({ onCreate, current, onSelect, inline }) {
  const [q, setQ] = useState(current?.organization_name || "");
  const [items, setItems] = useState([]);
  const [manual, setManual] = useState({ name: "", category_slug: "" });

  useEffect(() => {
    const t = setTimeout(() => {
      api.get(`/search/suggest?q=${encodeURIComponent(q)}`).then((r) => setItems(r.data.items));
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  const pick = async (org) => {
    if (inline) {
      onSelect({ organization_id: org.id, organization_name: org.name, category_slug: (org.categories || [])[0] || "" });
    } else {
      const { data } = await api.post("/cases", {
        organization_id: org.id,
        organization_name: org.name,
        category_slug: (org.categories || [])[0] || "",
      });
      // Advance immediately to step 2 so user doesn't have to re-pick
      try {
        await api.patch(`/cases/${data.id}`, { step: 2 });
      } catch (err) {
        console.error("Failed to update case step:", err);
      }
      onCreate(data.id, 2);
    }
  };

  const submitManual = async () => {
    if (!manual.name) return;
    if (inline) {
      onSelect({ organization_id: null, organization_name: manual.name, category_slug: manual.category_slug });
    } else {
      const { data } = await api.post("/cases", { organization_name: manual.name, category_slug: manual.category_slug });
      try {
        await api.patch(`/cases/${data.id}`, { step: 2 });
      } catch (err) {
        console.error("Failed to update case step:", err);
      }
      onCreate(data.id, 2);
    }
  };

  return (
    <div className="card-elevated p-6" data-testid="step-org">
      <p className="text-sm text-muted-foreground mb-4">Recherchez l'organisme que vous souhaitez résilier.</p>
      <div className="flex items-center gap-2 border border-border rounded-md p-2 pl-4 mb-4">
        <Search className="h-4 w-4 text-muted-foreground" />
        <Input data-testid="org-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Free, Netflix, EDF..." className="border-0 shadow-none focus-visible:ring-0" autoFocus />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-96 overflow-auto">
        {items.map((o) => (
          <button
            key={o.slug}
            onClick={() => pick(o)}
            data-testid={`org-pick-${o.slug}`}
            className="text-left p-3 rounded-md border border-border hover:bg-secondary transition-colors duration-200"
          >
            <div className="font-medium">{o.name}</div>
            <div className="text-xs text-muted-foreground">{(o.categories || []).join(", ")}</div>
          </button>
        ))}
      </div>
      <div className="mt-6 pt-6 border-t border-border">
        <div className="text-sm font-semibold mb-2">Vous ne trouvez pas votre organisme ?</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <Input placeholder="Nom de l'organisme" value={manual.name} onChange={(e) => setManual({ ...manual, name: e.target.value })} data-testid="manual-org-name" />
          <Input placeholder="Catégorie (ex : mobile)" value={manual.category_slug} onChange={(e) => setManual({ ...manual, category_slug: e.target.value })} data-testid="manual-org-category" />
          <Button onClick={submitManual} disabled={!manual.name} data-testid="manual-org-submit">Ajouter</Button>
        </div>
      </div>
    </div>
  );
}

// ---- STEP 2: contract info ----
function StepContract({ caseData, onNext, onBack }) {
  const [info, setInfo] = useState(caseData.contract_info || {});
  const u = (k) => (e) => setInfo({ ...info, [k]: e.target.value });
  return (
    <div className="card-elevated p-6 space-y-4" data-testid="step-contract">
      <p className="text-sm text-muted-foreground">Renseignez les informations contractuelles (autant que possible).</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div><Label>Numéro de contrat</Label><Input value={info.contract_number || ""} onChange={u("contract_number")} data-testid="contract-number" /></div>
        <div><Label>Numéro de client</Label><Input value={info.client_number || ""} onChange={u("client_number")} data-testid="client-number" /></div>
        <div><Label>Numéro de ligne / adhésion</Label><Input value={info.line_number || ""} onChange={u("line_number")} /></div>
        <div><Label>Numéro de police</Label><Input value={info.policy_number || ""} onChange={u("policy_number")} /></div>
        <div><Label>Date de souscription</Label><Input type="date" value={info.subscription_date || ""} onChange={u("subscription_date")} /></div>
        <div><Label>Date d'échéance</Label><Input type="date" value={info.due_date || ""} onChange={u("due_date")} /></div>
        <div><Label>Montant mensuel (€)</Label><Input type="number" value={info.monthly_amount || ""} onChange={u("monthly_amount")} /></div>
        <div><Label>Immatriculation / référence spécifique</Label><Input value={info.reference || ""} onChange={u("reference")} /></div>
      </div>
      <StepNav onBack={onBack} onNext={() => onNext({ contract_info: info })} />
    </div>
  );
}

// ---- STEP 3: personal info ----
function StepPersonal({ caseData, onNext, onBack }) {
  const [p, setP] = useState(caseData.personal_info || {});
  const u = (k) => (e) => setP({ ...p, [k]: e.target.value });
  const addressPatch = (patch) => setP((prev) => ({ ...prev, ...patch }));
  const ok = p.civility && p.first_name && p.last_name && p.address && p.postal_code && p.city && p.email;
  return (
    <div className="card-elevated p-6 space-y-4" data-testid="step-personal">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          <Label>Civilité *</Label>
          <select className="w-full border border-border rounded-md h-10 px-3 bg-background text-sm" value={p.civility || ""} onChange={u("civility")} required data-testid="personal-civility">
            <option value="">—</option><option>Madame</option><option>Monsieur</option>
          </select>
        </div>
        <div className="md:col-span-2"><Label>Prénom *</Label><Input value={p.first_name || ""} onChange={u("first_name")} required data-testid="personal-firstname" /></div>
        <div className="md:col-span-1"><Label>Nom *</Label><Input value={p.last_name || ""} onChange={u("last_name")} required data-testid="personal-lastname" /></div>
      </div>
      <AddressAutocomplete
        value={{ address: p.address, postal_code: p.postal_code, city: p.city, country: p.country }}
        onChange={addressPatch}
        testidPrefix="personal-address"
      />
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="md:col-span-2"><Label>Email *</Label><Input type="email" value={p.email || ""} onChange={u("email")} required data-testid="personal-email" /></div>
        <div className="md:col-span-2"><Label>Téléphone</Label><Input type="tel" value={p.phone || ""} onChange={u("phone")} data-testid="personal-phone" /></div>
      </div>
      <StepNav onBack={onBack} onNext={() => onNext({ personal_info: p })} disabled={!ok} />
    </div>
  );
}

// ---- STEP 4: reason ----
function StepReason({ caseData, onNext, onBack }) {
  const [reason, setReason] = useState(caseData.reason || "");
  const [detail, setDetail] = useState(caseData.reason_detail || "");
  return (
    <div className="card-elevated p-6 space-y-4" data-testid="step-reason">
      <p className="text-sm text-muted-foreground">Quel est le motif principal de votre résiliation ?</p>
      <RadioGroup value={reason} onValueChange={setReason} className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {REASONS.map((r) => (
          <label key={r.v} className={`flex items-center gap-2 border border-border rounded-md p-3 cursor-pointer transition-colors duration-200 ${reason === r.v ? "bg-primary text-primary-foreground border-primary" : "hover:bg-secondary"}`} data-testid={`reason-${r.v}`}>
            <RadioGroupItem value={r.v} />
            <span className="text-sm">{r.l}</span>
          </label>
        ))}
      </RadioGroup>
      <div>
        <Label>Précisions (facultatif)</Label>
        <Textarea rows={4} value={detail} onChange={(e) => setDetail(e.target.value)} data-testid="reason-detail" placeholder="Détails supplémentaires que vous souhaitez inclure dans la lettre..." />
      </div>
      <StepNav onBack={onBack} onNext={() => onNext({ reason, reason_detail: detail })} disabled={!reason} />
    </div>
  );
}

// ---- STEP 5: analysis ----
function StepAnalysis({ caseData, onNext, onBack }) {
  const notice = reasonToNotice(caseData.reason);
  const docs = reasonToDocs(caseData.reason);
  return (
    <div className="space-y-4" data-testid="step-analysis">
      <div className="card-elevated p-6">
        <div className="flex items-center gap-2 mb-3">
          <Check className="h-5 w-5 text-green-600" />
          <div className="font-semibold">Procédure recommandée : Lettre recommandée avec accusé de réception</div>
        </div>
        <p className="text-sm text-muted-foreground">Notre analyse recommande l'envoi d'une lettre recommandée pour disposer d'une preuve officielle.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="card-elevated p-5">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">Préavis estimé</div>
          <div className="text-xl font-semibold">{notice}</div>
        </div>
        <div className="card-elevated p-5">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">Date d'effet estimée</div>
          <div className="text-xl font-semibold font-mono">{estimatedEffectDate(caseData.reason)}</div>
        </div>
      </div>
      <div className="card-elevated p-5">
        <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">Justificatifs recommandés</div>
        <ul className="space-y-1 text-sm">
          {docs.map((d) => <li key={d} className="flex items-center gap-2"><FileText className="h-3 w-3 text-muted-foreground" />{d}</li>)}
        </ul>
      </div>
      <div className="p-4 rounded-md bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-900/50 flex gap-3 text-sm">
        <AlertCircle className="h-4 w-4 text-yellow-700 dark:text-yellow-500 shrink-0 mt-0.5" />
        <p className="text-yellow-900 dark:text-yellow-200">Cette analyse est fournie à titre informatif à partir des données saisies. Vérifiez également les conditions particulières de votre contrat. Résiliation Express ne fournit pas de conseil juridique personnalisé.</p>
      </div>
      <StepNav onBack={onBack} onNext={onNext} />
    </div>
  );
}

// ---- STEP 6: documents ----
function StepDocuments({ caseId, caseData, refresh, onNext, onBack }) {
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/cases/${caseId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      toast.success("Document ajouté");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload échoué");
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const remove = async (id) => {
    await api.delete(`/cases/${caseId}/documents/${id}`);
    refresh();
  };

  return (
    <div className="card-elevated p-6 space-y-4" data-testid="step-documents">
      <p className="text-sm text-muted-foreground">Joignez vos justificatifs (PDF, JPG, PNG, HEIC — 8 Mo max par fichier).</p>
      <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
        <Upload className="h-8 w-8 mx-auto mb-3 text-muted-foreground" />
        <p className="text-sm text-muted-foreground mb-3">Glissez-déposez ou cliquez pour ajouter</p>
        <input ref={fileRef} type="file" onChange={handleFile} accept="application/pdf,image/*" className="hidden" data-testid="doc-file-input" />
        <Button onClick={() => fileRef.current?.click()} disabled={uploading} variant="outline" data-testid="doc-upload-btn">
          {uploading ? "Envoi..." : "Ajouter un document"}
        </Button>
      </div>
      <div className="space-y-2">
        {(caseData.documents || []).map((d) => (
          <div key={d.id} className="flex items-center justify-between border border-border rounded-md p-3" data-testid={`doc-${d.id}`}>
            <div className="flex items-center gap-3">
              <FileText className="h-4 w-4 text-muted-foreground" />
              <div>
                <div className="text-sm font-medium">{d.name}</div>
                <div className="text-xs text-muted-foreground font-mono">{Math.round(d.size / 1024)} Ko · {d.mime}</div>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={() => remove(d.id)} data-testid={`doc-remove-${d.id}`}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
      </div>
      <StepNav onBack={onBack} onNext={onNext} />
    </div>
  );
}

// ---- STEP 7: letter ----
function StepLetter({ caseId, caseData, refresh, onNext, onBack }) {
  const [text, setText] = useState(caseData.letter_content || "");
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/cases/${caseId}/letter/generate`);
      setText(data.text);
      await refresh();
      toast.success("Lettre générée");
      pushAnalyticsEvent("letter_generated", { organization_category: caseData.category_slug }, { once: true, key: `letter_${caseId}` });
    } catch { toast.error("Erreur de génération"); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (!text) generate(); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    await api.patch(`/cases/${caseId}`, { letter_content: text });
    toast.success("Modifications sauvegardées");
  };

  return (
    <div className="space-y-4" data-testid="step-letter">
      <div className="card-elevated p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm font-semibold">Aperçu du courrier</div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={generate} disabled={loading} data-testid="letter-regen-btn">Régénérer</Button>
            <Button variant="outline" size="sm" onClick={save} data-testid="letter-save-btn">Sauvegarder</Button>
          </div>
        </div>
        <Textarea rows={22} value={text} onChange={(e) => setText(e.target.value)} className="font-mono text-sm" data-testid="letter-textarea" />
      </div>
      <StepNav onBack={onBack} onNext={onNext} disabled={!text} />
    </div>
  );
}

// ---- STEP 8: signature ----
function StepSignature({ caseData, onNext, onBack }) {
  const canvasRef = useRef();
  const containerRef = useRef();
  const [typed, setTyped] = useState(caseData.signature?.text || "");
  const [mode, setMode] = useState(caseData.signature?.type || "drawn");
  const [hasStroke, setHasStroke] = useState(false);
  const [canvasWidth, setCanvasWidth] = useState(600);

  // Responsive canvas: measure container to set proper pixel size (fixes mobile touch drawing)
  useEffect(() => {
    if (mode !== "drawn") return;
    const measure = () => {
      if (containerRef.current) {
        const w = Math.max(280, Math.floor(containerRef.current.getBoundingClientRect().width));
        setCanvasWidth(w);
      }
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [mode]);

  const clear = () => {
    if (canvasRef.current) {
      canvasRef.current.clear();
      setHasStroke(false);
    }
  };

  const submit = () => {
    let sig = null;
    if (mode === "drawn") {
      if (!canvasRef.current || canvasRef.current.isEmpty()) { toast.error("Veuillez dessiner votre signature"); return; }
      sig = { type: "drawn", data_b64: canvasRef.current.getCanvas().toDataURL("image/png"), at: new Date().toISOString() };
    } else {
      if (!typed.trim()) { toast.error("Veuillez saisir votre nom"); return; }
      sig = { type: "typed", text: typed, at: new Date().toISOString() };
    }
    onNext({ signature: sig });
  };

  return (
    <div className="card-elevated p-6 space-y-4" data-testid="step-signature">
      <div className="flex gap-2">
        <Button variant={mode === "drawn" ? "default" : "outline"} onClick={() => setMode("drawn")} data-testid="sig-mode-drawn">Dessinée</Button>
        <Button variant={mode === "typed" ? "default" : "outline"} onClick={() => setMode("typed")} data-testid="sig-mode-typed">Tapée</Button>
      </div>
      {mode === "drawn" ? (
        <div>
          <div ref={containerRef} className="border border-border rounded-md bg-white overflow-hidden" style={{ touchAction: "none" }}>
            <SignatureCanvas
              ref={canvasRef}
              penColor="black"
              onEnd={() => setHasStroke(canvasRef.current && !canvasRef.current.isEmpty())}
              canvasProps={{
                width: canvasWidth,
                height: 200,
                className: "signature-canvas block",
                style: { touchAction: "none", display: "block", width: "100%", height: 200 },
                "data-testid": "sig-canvas",
              }}
            />
          </div>
          <div className="flex items-center justify-between mt-2">
            <p className="text-xs text-muted-foreground">
              {hasStroke ? "Signature enregistrée. Cliquez sur Suivant." : "Utilisez la souris ou le doigt pour signer ci-dessus."}
            </p>
            <Button variant="ghost" size="sm" onClick={clear} data-testid="sig-clear-btn">Effacer</Button>
          </div>
        </div>
      ) : (
        <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="Prénom Nom" className="font-serif italic text-xl h-14" data-testid="sig-typed-input" />
      )}
      <div className="text-xs text-muted-foreground">En signant, vous certifiez que les informations sont exactes et vous autorisez Résiliation Express à traiter votre demande.</div>
      <StepNav onBack={onBack} onNext={submit} />
    </div>
  );
}

// ---- STEP 9: offer + submit for human review ----
function StepOffer({ caseId, caseData, onBack, saveCase }) {
  const [offer, setOffer] = useState(caseData.offer || "download");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const nav = useNavigate();

  const submit = async () => {
    if (submitting) return; // double-click guard
    setSubmitting(true);
    setError(null);
    try {
      await saveCase({ offer, step: 9 });
      await api.post(`/cases/${caseId}/submit-for-review`);
      pushAnalyticsEvent("case_submitted", { service_type: offer }, { once: true, key: `submit_${caseId}` });
      nav(`/dossier/soumis?case=${caseId}`, { replace: true });
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      const status = e?.response?.status;
      let msg = "La transmission de votre dossier n'a pas pu être finalisée. Votre brouillon reste sauvegardé. Veuillez réessayer.";
      if (status === 401) msg = "Veuillez vous connecter avant de soumettre votre dossier.";
      else if (status === 400 && detail) msg = detail;
      else if (status === 403) msg = "Vous n'êtes pas autorisé à soumettre ce dossier.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="step-offer">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { k: "download", n: "Offre sur mesure", p: "50 % de la cotisation annuelle", d: "Devis personnalisé calculé sur la base de 50 % de la cotisation annuelle du contrat à résilier. Facturation séparée après validation." },
          { k: "assisted", n: "Envoi assisté", p: "9,90 € TTC", d: "Après vérification, nous envoyons le courrier en recommandé et archivons les preuves." },
          { k: "premium", n: "Accompagnement complet", p: "14,90 € TTC", d: "Après vérification, envoi assisté + traitement prioritaire + relance." },
        ].map((o) => (
          <button
            key={o.k}
            type="button"
            onClick={() => setOffer(o.k)}
            data-testid={`offer-${o.k}`}
            className={`text-left card-elevated p-6 border-2 transition-colors duration-200 ${offer === o.k ? "border-primary" : "border-transparent"}`}
          >
            <div className="text-sm text-muted-foreground">{o.n}</div>
            <div className="text-2xl font-bold my-2">{o.p}</div>
            <div className="text-sm text-muted-foreground">{o.d}</div>
          </button>
        ))}
      </div>

      <div className="rounded-lg border border-border p-4 bg-secondary/40 text-sm text-muted-foreground leading-relaxed">
        <strong className="text-foreground">Vérification humaine avant traitement.</strong> Aucun paiement n'est demandé à cette étape.
        Votre dossier sera examiné par un conseiller. Vous serez notifié dès qu'il aura été vérifié — ou si une information
        complémentaire est nécessaire.
      </div>

      {error && (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive" role="alert" data-testid="submit-error">
          {error}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 pt-2">
        <Button variant="outline" onClick={onBack} disabled={submitting} data-testid="submit-back-btn">Retour</Button>
        <Button
          onClick={submit}
          disabled={submitting || !caseData?.organization_name || !caseData?.personal_info}
          className="flex-1 bg-gradient-brand text-white border-0 hover:opacity-90"
          data-testid="submit-for-review-btn"
        >
          {submitting ? "Transmission en cours…" : "Soumettre mon dossier pour vérification"}
        </Button>
      </div>
    </div>
  );
}

// ---- STEP 10: payment (only reachable after admin approves — status = approved_for_payment) ----
function StepPayment({ caseId, caseData, config, refresh, onSuccess, onBack }) {
  const [orderId, setOrderId] = useState(null);
  const [demoConfirming, setDemoConfirming] = useState(false);

  const price = { download: 0, assisted: 990, premium: 1490 }[caseData.offer] || 0;

  useEffect(() => {
    if (price > 0) {
      pushAnalyticsEvent("checkout_started", { value: price / 100, currency: "EUR", service_type: caseData.offer }, { once: true, key: `checkout_${caseId}` });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  const createOrder = async () => {
    const { data } = await api.post("/payments/paypal/create-order", { caseId });
    setOrderId(data.orderId);
    pushAnalyticsEvent("paypal_order_created", { value: (price / 100), currency: "EUR", service_type: caseData.offer }, { once: false });
    return data.orderId;
  };

  const capture = async (id) => {
    try {
      const { data } = await api.post("/payments/paypal/capture-order", { orderId: id });
      if (data.status === "PAYMENT_COMPLETED") {
        await refresh();
        toast.success("Paiement confirmé");
        pushAnalyticsEvent("payment_completed", {
          value: (price / 100),
          currency: "EUR",
          service_type: caseData.offer,
          organization_category: caseData.category_slug,
        }, { once: true, key: `pay_ok_${id}` });
        onSuccess();
      } else if (data.status === "PAYMENT_CAPTURE_PENDING") {
        await refresh();
        toast.info("Paiement en cours de confirmation. Vous serez notifié.");
        pushAnalyticsEvent("payment_failed", { failure_type: "pending" }, { once: false });
        onSuccess();
      } else {
        toast.error("Paiement non complété");
        pushAnalyticsEvent("payment_failed", { failure_type: "unknown" }, { once: false });
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Erreur PayPal";
      const code = e?.response?.status;
      let failure_type = "technical_error";
      if (code === 400) failure_type = "denied";
      toast.error(msg);
      pushAnalyticsEvent("payment_failed", { failure_type }, { once: false });
      throw e;
    }
  };

  const handleDemo = async () => {
    toast.error("Le paiement PayPal Live est configuré : suivez les instructions à l'écran.");
    setDemoConfirming(false);
  };

  const handleDownload = async () => {
    // For download offer, price is 0, just skip payment
    if (price === 0) {
      // For 'download' offer we mark as no payment needed and go to done
      toast.success("Votre lettre est prête au téléchargement");
      onSuccess();
    }
  };

  if (caseData.offer === "download" || price === 0) {
    return (
      <div className="card-elevated p-6 space-y-4" data-testid="step-payment-free">
        <p className="text-sm text-muted-foreground">Formule « Offre sur mesure » — devis calculé sur 50 % de la cotisation annuelle du contrat à résilier.</p>
        <Button onClick={handleDownload} className="bg-gradient-brand text-white border-0 hover:opacity-90" data-testid="download-continue-btn">
          Générer et télécharger ma lettre
        </Button>
        <StepNavOnlyBack onBack={onBack} />
      </div>
    );
  }

  const isDemo = config?.demo_mode || !config?.paypal?.client_id;

  return (
    <div className="card-elevated p-6 space-y-6" data-testid="step-payment">
      <div className="flex items-center justify-between border-b border-border pb-4">
        <div>
          <div className="text-sm text-muted-foreground">À régler</div>
          <div className="text-3xl font-bold font-mono">{fmtEur(price)}</div>
        </div>
        <Badge variant="outline">PayPal Business</Badge>
      </div>

      {isDemo ? (
        <div className="space-y-3">
          <div className="p-4 rounded-md bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-900/50 flex gap-3 text-sm">
            <AlertCircle className="h-4 w-4 text-yellow-700 shrink-0 mt-0.5" />
            <p>Passerelle PayPal non disponible dans cet environnement. Réessayez plus tard ou contactez le support.</p>
          </div>
        </div>
      ) : (
        <>
          {config.paypal.live_validation_mode && (
            <div className="p-3 rounded-md bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900/50 text-xs text-blue-900 dark:text-blue-200">
              Validation PayPal Live en cours — votre paiement sera réel, l'envoi postal sera préparé après validation administrative.
            </div>
          )}
          <PayPalScriptProvider
          options={{
            "client-id": config.paypal.client_id,
            currency: config.paypal.currency || "EUR",
            "enable-funding": "paylater,card",
            components: "buttons,messages",
            locale: "fr_FR",
          }}
        >
          {config.paypal.pay_later_enabled && (
            <div className="mb-4">
              <PayPalMessages amount={(price / 100).toFixed(2)} pageType="checkout" style={{ layout: "text", logo: { type: "primary" } }} />
              <p className="text-xs text-muted-foreground mt-1">{config.paypal.pay_later_label} — sous réserve d'éligibilité et d'acceptation par PayPal.</p>
            </div>
          )}
          <PayPalButtons
            style={{ layout: "vertical", label: "pay" }}
            createOrder={async () => await createOrder()}
            onApprove={async (data) => await capture(data.orderID)}
            onError={(err) => { console.error(err); toast.error("Erreur PayPal"); }}
            onCancel={() => toast.info("Paiement annulé")}
            data-testid="paypal-buttons"
          />
        </PayPalScriptProvider>
        </>
      )}

      <StepNavOnlyBack onBack={onBack} />
    </div>
  );
}

// ---- STEP 11: done ----
function StepDone({ caseData }) {
  return (
    <div className="card-elevated p-8 text-center" data-testid="step-done">
      <div className="mx-auto h-16 w-16 rounded-full bg-green-100 dark:bg-green-950/30 flex items-center justify-center mb-4">
        <Check className="h-8 w-8 text-green-600 dark:text-green-400" />
      </div>
      <h2 className="text-2xl font-bold tracking-tight mb-2">Votre dossier est traité.</h2>
      <p className="text-muted-foreground mb-6">
        {caseData.tracking_number ? (<>Votre courrier a été soumis au prestataire postal. Numéro de suivi : <span className="font-mono">{caseData.tracking_number}</span></>) : "Votre lettre est disponible dans votre espace."}
      </p>
      <div className="flex flex-wrap gap-3 justify-center">
        <Button asChild data-testid="done-view-case-btn"><Link to={`/espace/dossiers/${caseData.id}`}>Voir le dossier</Link></Button>
        <Button asChild variant="outline" data-testid="done-download-letter-btn">
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/cases/${caseData.id}/letter/pdf`} target="_blank" rel="noreferrer">Télécharger la lettre</a>
        </Button>
      </div>
    </div>
  );
}

// ---- utils ----
function StepNav({ onBack, onNext, disabled }) {
  return (
    <div className="flex justify-between pt-4 mt-4 border-t border-border">
      <Button variant="ghost" onClick={onBack} data-testid="step-back-btn"><ChevronLeft className="h-4 w-4 mr-1" />Précédent</Button>
      <Button onClick={onNext} disabled={disabled} className="bg-gradient-brand text-white border-0 hover:opacity-90" data-testid="step-next-btn">
        Suivant<ChevronRight className="h-4 w-4 ml-1" />
      </Button>
    </div>
  );
}

function StepNavOnlyBack({ onBack }) {
  return (
    <div className="flex justify-between pt-4 mt-4 border-t border-border">
      <Button variant="ghost" onClick={onBack} data-testid="step-back-btn"><ChevronLeft className="h-4 w-4 mr-1" />Précédent</Button>
    </div>
  );
}

function reasonToNotice(r) {
  return {
    echeance: "2 mois avant l'échéance",
    fin_engagement: "1 à 2 mois",
    un_an: "30 jours (résiliation immédiate)",
    demenagement: "3 mois maximum",
    vente: "10 jours à 1 mois",
    changement_situation: "3 mois",
    augmentation: "15 jours à réception avis",
    modification: "30 jours",
    nouvel_assureur: "1 mois (nouvel assureur s'en charge)",
    service_defaillant: "Immédiat après mise en demeure",
    retractation: "14 jours",
    autre: "Variable",
  }[r] || "Variable";
}

function reasonToDocs(r) {
  const base = ["Copie de la pièce d'identité (recommandé)"];
  const extra = {
    demenagement: ["Justificatif de domicile (facture, bail)"],
    vente: ["Certificat de vente ou preuve de cession"],
    changement_situation: ["Justificatif du changement (situation professionnelle, familiale)"],
    augmentation: ["Copie de l'avis d'augmentation"],
    nouvel_assureur: ["Attestation du nouvel assureur"],
    retractation: ["Aucun justificatif nécessaire"],
  }[r] || [];
  return [...base, ...extra];
}

function estimatedEffectDate(r) {
  const d = new Date();
  const days = { echeance: 60, fin_engagement: 45, un_an: 30, demenagement: 30, vente: 15, changement_situation: 30, augmentation: 30, modification: 30, nouvel_assureur: 30, service_defaillant: 15, retractation: 14, autre: 30 }[r] || 30;
  d.setDate(d.getDate() + days);
  return d.toLocaleDateString("fr-FR");
}
